import React, { createContext, useCallback, useContext, useReducer, useEffect, useRef } from 'react';
import api, { authAPI, cartAPI, getDeviceId } from '../services/api';
import {
  getAccessToken,
  setAccessToken,
  getRefreshToken,
  setRefreshToken,
  getUserBlob,
  setUserBlob,
  clearAllAuth,
} from '../utils/authStorage';

const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: true,
  error: null
};

const AUTH_ACTIONS = {
  LOGIN_START: 'LOGIN_START',
  LOGIN_SUCCESS: 'LOGIN_SUCCESS',
  LOGIN_FAILURE: 'LOGIN_FAILURE',
  LOGOUT: 'LOGOUT',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_LOADING: 'SET_LOADING'
};

const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.LOGIN_START:
      return {
        ...state,
        loading: true,
        error: null
      };
    case AUTH_ACTIONS.LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false,
        error: null
      };
    case AUTH_ACTIONS.LOGIN_FAILURE:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        error: action.payload
      };
    case AUTH_ACTIONS.LOGOUT:
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        error: null
      };
    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };
    default:
      return state;
  }
};

// Create context
const AuthContext = createContext();

// Provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check if user is authenticated on app load
  useEffect(() => {
    // پل cross-origin (production: فرانت روی origin جدا) — فقط وقتی ?bridge_ticket=
    // در URL هست. TICKET یک‌بارمصرف و کوتاه‌مدت است (نه JWT). آن را با
    // POST /auth/bridge-exchange/ با JWT واقعی مبادله می‌کنیم — JWT هیچ‌وقت
    // در URL/history/log نمی‌ماند (HIGH-3). در تک‌origin (dev) این query نیست.
    try {
      const qs = new URLSearchParams(window.location.search);
      const bridgeTicket = qs.get('bridge_ticket');
      if (bridgeTicket) {
        try {
          window.history.replaceState({}, '', window.location.pathname);
        } catch (_) {}
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
        (async () => {
          try {
            const res = await authAPI.exchangeBridge(bridgeTicket);
            const data = res && res.data;
            if (data && data.token && data.user) {
              setAccessToken(data.token);
              if (data.refresh) setRefreshToken(data.refresh);
              setUserBlob(data.user);
              dispatch({
                type: AUTH_ACTIONS.LOGIN_SUCCESS,
                payload: { user: data.user, token: data.token },
              });
              Promise.resolve()
                .then(() => cartAPI.mergeGuestCartIntoServerCart())
                .catch(() => {});
            } else {
              dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
            }
          } catch (_) {
            dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
          }
        })();
        return;
      }
    } catch (_) {}

    const token = getAccessToken();
    const user = getUserBlob();

    if (token && user) {
      try {
        const parsedUser = JSON.parse(user);
        dispatch({
          type: AUTH_ACTIONS.LOGIN_SUCCESS,
          payload: { user: parsedUser, token }
        });
      } catch (error) {
        clearAllAuth();
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
      return;
    }

    // پلِ هویت staff (تک‌origin): وقتی توکن localStorage وجود ندارد، بک‌اند را
    // با کوکی سشن ادمین جنگو می‌پرسیم (GET /api/auth/session-user/).
    // چون فرانت + بک‌اند روی یک origin هستند (8000)، کوکی same-origin ارسال
    // می‌شود و پل خودکار کار می‌کند — بدون bridge_token در URL.
    // اگر staff معتبر بود JWT می‌گیرد و مثل مشتری لاگین می‌شود؛
    // در غیر این‌صورت (401) کاربر عادی/مهمان است و در حالت guest می‌ماند.
    //
    // فاز: guard — فقط وقتی نشانهٔ session ادمین جنگو (cookie `csrftoken`،
    // non-HttpOnly) روی origin بک‌اند وجود داشته باشد، session-user/ را بزنیم.
    // مشتری عادی که هرگز وارد ادمین جنگو نشده csrftoken ندارد → call نمی‌شود
    // → 401 در console ظاهر نمی‌شود. `sessionid` HttpOnly است و قابلِ خواندن از
    // document.cookie نیست؛ `csrftoken` تنها نشانهٔ موجود است.
    let cancelled = false;
    (async () => {
      const hasCsrftoken =
        typeof document !== 'undefined' &&
        document.cookie.indexOf('csrftoken=') !== -1;
      if (!hasCsrftoken) {
        if (!cancelled) {
          dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
        }
        return; // guest; staff bridge not applicable
      }
      try {
        const res = await authAPI.getSessionUser();
        if (cancelled) return;
        const data = res && res.data;
        const nextToken = data && data.token;
        const nextUser = data && data.user;
        if (nextToken && nextUser) {
          try {
            setAccessToken(nextToken);
            if (data.refresh) setRefreshToken(data.refresh);
            setUserBlob(nextUser);
          } catch (_) {}
          dispatch({
            type: AUTH_ACTIONS.LOGIN_SUCCESS,
            payload: { user: nextUser, token: nextToken }
          });
          Promise.resolve()
            .then(() => cartAPI.mergeGuestCartIntoServerCart())
            .catch(() => {});
          return;
        }
      } catch (_) {
        // 401 یا خطا → staff نیستیم؛ در حالت guest می‌مانیم
      }
      if (!cancelled) {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = () => {
      try {
        clearAllAuth();
      } catch (_) {}
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    };
    const storageHandler = (e) => {
      if (!e || !e.key) return;
      if (['token','refresh_token','user'].includes(e.key)) {
        const token = getAccessToken();
        const userStr = getUserBlob();
        if (token && userStr) {
          try {
            const u = JSON.parse(userStr);
            dispatch({ type: AUTH_ACTIONS.LOGIN_SUCCESS, payload: { user: u, token } });
          } catch {}
        } else if (!token) {
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
        }
      }
    };
    const visHandler = () => {
      if (document.visibilityState === 'visible') {
        const token = getAccessToken();
        if (token) scheduleProactiveRefresh();
      }
    };
    try {
      window.addEventListener('kadolar:auth-invalid', handler);
      window.addEventListener('storage', storageHandler);
      document.addEventListener('visibilitychange', visHandler);
    } catch (_) {}
    return () => {
      try {
        window.removeEventListener('kadolar:auth-invalid', handler);
        window.removeEventListener('storage', storageHandler);
        document.removeEventListener('visibilitychange', visHandler);
      } catch (_) {}
    };
  }, []);

  const login = (userData, token, refreshToken) => {
    setAccessToken(token);
    if (refreshToken) {
      setRefreshToken(refreshToken);
    }
    setUserBlob(userData);
    dispatch({
      type: AUTH_ACTIONS.LOGIN_SUCCESS,
      payload: { user: userData, token }
    });
    Promise.resolve()
      .then(() => cartAPI.mergeGuestCartIntoServerCart())
      .catch(() => {});
  };

  const logout = () => {
    try {
      const p = authAPI.logout();
      if (p && typeof p.catch === 'function') p.catch(() => {});
      if (p && typeof p.finally === 'function') {
        p.finally(() => {
          try {
            clearAllAuth();
          } catch (_) {}
          dispatch({ type: AUTH_ACTIONS.LOGOUT });
        });
        return;
      }
    } catch (_) {}
    clearAllAuth();
    dispatch({ type: AUTH_ACTIONS.LOGOUT });
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  // Set loading function
  const setLoading = (loading) => {
    dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: loading });
  };

  // Set error function
  const setError = (error) => {
    dispatch({ type: AUTH_ACTIONS.LOGIN_FAILURE, payload: error });
  };

  const refreshTimerRef = useRef(null);
  const scheduleProactiveRefresh = useCallback(() => {
    try {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      const token = state.token || getAccessToken();
      if (!token) return;
      const payload = JSON.parse(atob(token.split('.')[1] || ''));
      const expSec = payload?.exp;
      if (!expSec) return;
      const nowMs = Date.now();
      const expMs = expSec * 1000;
      const safetyMs = 60 * 1000;
      const delay = Math.max(expMs - nowMs - safetyMs, 15 * 1000);
      refreshTimerRef.current = setTimeout(async () => {
        const refresh = getRefreshToken();
        if (!refresh) return;
        try {
          const res = await api.post('/auth/token/refresh/', { refresh, device_id: getDeviceId() });
          const nextToken = res && res.data && res.data.access;
          const nextRefresh = res && res.data && res.data.refresh;
          if (nextToken) {
            setAccessToken(nextToken);
            if (nextRefresh) {
              setRefreshToken(nextRefresh);
            }
            const userStr = getUserBlob();
            const userObj = userStr ? JSON.parse(userStr) : state.user;
            dispatch({
              type: AUTH_ACTIONS.LOGIN_SUCCESS,
              payload: { user: userObj, token: nextToken }
            });
            scheduleProactiveRefresh();
          }
        } catch (err) {
          // P1 review fix: هر خطا کاربر را خارج نمی‌کند — فقط ردِ قطعیِ
          // توکن (401/403 از سرور) به خروجی می‌انجامد؛ قطعی اینترنت یا 500
          // فقط تایمر را برای تلاش بعدی می‌گذارد (سبد/فرم کاربر نمی‌سوزد).
          const status = err && err.response ? err.response.status : null;
          const isAuthFailure = status === 401 || status === 403;
          if (isAuthFailure) {
            try {
              clearAllAuth();
            } catch (_) {}
            dispatch({ type: AUTH_ACTIONS.LOGOUT });
            return;
          }
          scheduleProactiveRefresh();
        }
      }, delay);
    } catch (_) {}
  }, [state.token, state.user]);

  useEffect(() => {
    scheduleProactiveRefresh();
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [state.token, state.isAuthenticated, scheduleProactiveRefresh]);

  const value = {
    ...state,
    login,
    logout,
    clearError,
    setLoading,
    setError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
